import { interpolateRgb } from 'd3-interpolate'
import { scaleLinear } from 'd3-scale'
import { useMemo } from 'react'

import type { AreaGraphElement, FillFunction } from '@/features/bidding/components/cascading/charts/types'
import { getMarketColor } from '@/features/bidding/constants/marketColors'
import type { BidLevelInput } from '@/features/bidding/types/bidLadderInput'
import type { ForecastMarketPair } from '@/features/bidding/types/forecastPricing'

const HOURS_PER_DAY = 24

export type GroupVolume = {
  uuid: string
  code: string
  direction: 'up' | 'down'
  capacityMW: number
  levelIndex: number
}

type UseHourlyVolumeDataByGroupParams = {
  levels: BidLevelInput[]
  market: ForecastMarketPair
  baseDate: Date
}

type UseHourlyVolumeDataByGroupResult = {
  data: AreaGraphElement[]
  datetimes: Date[]
  layerKeys: string[]
  layerFill: FillFunction
  layerKeyToGroupUuid: Map<string, string>
  groupVolumes: GroupVolume[]
}

/**
 * Transforms bid level inputs into AreaGraph-compatible format with
 * one layer per activation group (instead of per bid level).
 *
 * Used by InteractiveHourlyVolumeChart for per-group click-to-toggle.
 */
export function useHourlyVolumeDataByGroup({
  levels,
  market,
  baseDate,
}: UseHourlyVolumeDataByGroupParams): UseHourlyVolumeDataByGroupResult {
  return useMemo(() => {
    const baseColor = getMarketColor(market)

    // Collect all assigned groups across levels with their metadata
    const groupVolumes: GroupVolume[] = []
    for (let levelIndex = 0; levelIndex < levels.length; levelIndex++) {
      const level = levels[levelIndex]
      for (const group of level.assignedGroups) {
        groupVolumes.push({
          uuid: group.uuid,
          code: group.code,
          direction: group.direction,
          capacityMW: group.capacityMW,
          levelIndex,
        })
      }
    }

    // Build layer keys: "up_<uuid>" / "down_<uuid>" for each group
    // UP layers first, then DOWN layers (same ordering convention as useHourlyVolumeData)
    const upGroups = groupVolumes.filter((g) => g.direction === 'up')
    const downGroups = groupVolumes.filter((g) => g.direction === 'down')

    const upLayerKeys = upGroups.map((g) => `up_${g.uuid}`)
    const downLayerKeys = downGroups.map((g) => `down_${g.uuid}`)
    const layerKeys = [...upLayerKeys, ...downLayerKeys]

    // Build reverse mapping: layerKey -> groupUuid
    const layerKeyToGroupUuid = new Map<string, string>()
    for (const g of upGroups) {
      layerKeyToGroupUuid.set(`up_${g.uuid}`, g.uuid)
    }
    for (const g of downGroups) {
      layerKeyToGroupUuid.set(`down_${g.uuid}`, g.uuid)
    }

    // Create data points for each hour
    const data: AreaGraphElement[] = []
    const datetimes: Date[] = []

    for (let hour = 0; hour < HOURS_PER_DAY; hour++) {
      const datetime = new Date(baseDate)
      datetime.setHours(hour, 0, 0, 0)
      datetimes.push(datetime)

      const layerData: Record<string, number> = {}

      for (const g of upGroups) {
        layerData[`up_${g.uuid}`] = g.capacityMW
      }
      for (const g of downGroups) {
        layerData[`down_${g.uuid}`] = -g.capacityMW
      }

      data.push({
        datetime,
        meta: { hour, groupVolumes },
        layerData,
      })
    }

    // Color gradient: distribute colors across groups using level position
    const totalUp = upGroups.reduce((sum, g) => sum + g.capacityMW, 0)
    const totalDown = downGroups.reduce((sum, g) => sum + g.capacityMW, 0)
    const maxVolume = Math.max(totalUp, totalDown, 1)

    const lightColor = interpolateRgb('#ffffff', baseColor)(0.2)
    const interpolator = interpolateRgb(lightColor, baseColor)
    const colorScale = scaleLinear().domain([0, maxVolume]).range([0.3, 1]).clamp(true)

    // Pre-compute cumulative positions for color assignment
    const upCumulative = new Map<string, number>()
    let cumUp = 0
    for (const g of upGroups) {
      cumUp += g.capacityMW
      upCumulative.set(`up_${g.uuid}`, cumUp)
    }

    const downCumulative = new Map<string, number>()
    let cumDown = 0
    for (const g of downGroups) {
      cumDown += g.capacityMW
      downCumulative.set(`down_${g.uuid}`, cumDown)
    }

    const layerFill: FillFunction = (key) => () => {
      const cumValue = upCumulative.get(key) ?? downCumulative.get(key) ?? 0
      const t = colorScale(cumValue)
      return interpolator(t)
    }

    return { data, datetimes, layerKeys, layerFill, layerKeyToGroupUuid, groupVolumes }
  }, [levels, market, baseDate])
}
